import { useState } from 'react'
import { LogIn, X, AlertCircle } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { isSupabaseConfigured } from '@/repository/supabase/client'
import { SignInDialog } from '@/auth/SignInDialog'

/**
 * Persistent reminder that the user is in local-only demo mode and that
 * signing in unlocks cloud sync + the gated modules (Strategy Engine,
 * Cash Flow). Renders inside AppShell, just under the header.
 *
 * Visibility rules — the banner ONLY appears when:
 *   1. Supabase is configured for this build (no point nudging toward
 *      sign-in if there's no sign-in path available)
 *   2. The user is not authenticated
 *   3. The user has not dismissed within the last `DISMISS_TTL_DAYS` days
 *
 * Dismissal resets after the TTL so the nudge doesn't disappear forever
 * for users who initially weren't ready. Seven days is the default —
 * short enough to feel present, long enough to avoid being naggy.
 */

const DISMISS_KEY = 'debtos:demoBannerDismissedAt:v1'
const DISMISS_TTL_DAYS = 7

export function DemoModeBanner() {
  const { isAuthenticated, isLoading } = useAuth()
  const [signInOpen, setSignInOpen] = useState(false)
  // Dismissal state is initialised once at mount from localStorage. We
  // deliberately don't re-read on auth changes — a deliberate sign-out
  // inside the same session keeps the existing dismissal in effect, and
  // the next page load re-reads the TTL naturally. Avoiding a useEffect
  // here keeps the `react-hooks/set-state-in-effect` rule happy.
  const [hidden, setHidden] = useState<boolean>(() => isRecentlyDismissed())

  if (isLoading) return null
  if (!isSupabaseConfigured()) return null
  if (isAuthenticated) return null
  if (hidden) return null

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString())
    } catch {
      // private browsing / quota — banner just won't suppress this session
    }
    setHidden(true)
  }

  return (
    <>
      <div className="bg-amber-500/10 border-b border-amber-500/30">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-2 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 shrink-0 text-amber-400" />
          <div className="flex-1 min-w-0 text-xs sm:text-sm">
            <span className="font-semibold text-white">You&rsquo;re in demo mode.</span>{' '}
            <span className="text-amber-200/80">
              Sign in to unlock Strategy Engine, Cash Flow, and cloud sync across devices.
            </span>
          </div>
          <button
            onClick={() => setSignInOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-xs font-semibold transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" />
            Sign in
          </button>
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 text-amber-200/60 hover:text-white hover:bg-amber-500/10 rounded transition-colors"
            aria-label="Dismiss demo-mode banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  )
}

/** Returns true if the user dismissed the banner within the last TTL window. */
function isRecentlyDismissed(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = new Date(raw).getTime()
    if (Number.isNaN(dismissedAt)) return false
    const ageMs = Date.now() - dismissedAt
    return ageMs < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}
